import { useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logotype from "@/assets/stillwater-logotype-dark.svg";

const navLinks = [
  { label: "How Therapy Helps", href: "#how-therapy-helps" },
  { label: "About", href: "#about" },
  { label: "Approach", href: "#therapy-style" },
  { label: "What to Expect", href: "#what-to-expect" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const resolveHref = (hash: string) => isHome ? hash : `/${hash}`;

  const navRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const nav = navRef.current;
    if (!nav) return;
    const navRect = nav.getBoundingClientRect();
    const linkRect = e.currentTarget.getBoundingClientRect();
    setIndicator({
      left: linkRect.left - navRect.left,
      width: linkRect.width,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIndicator(null);
  }, []);

  return (
    <header className="site-header sticky top-0 z-50 w-full bg-background/90 backdrop-blur-sm border-b border-border">
      <div className="header-container max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
        <a href={isHome ? "#" : "/"} className="header-logo shrink-0">
          <img src={logotype} alt="Stillwater Therapy" className="h-5" />
        </a>

        {/* Desktop nav */}
        <nav
          ref={navRef}
          className="header-nav-desktop hidden lg:flex items-center gap-6 relative"
          onMouseLeave={handleMouseLeave}
        >
          {/* Sliding underline indicator */}
          <span
            className="absolute bottom-0 h-[2px] bg-primary rounded-full transition-all duration-300 ease-out pointer-events-none"
            style={{
              left: indicator ? indicator.left : 0,
              width: indicator ? indicator.width : 0,
              opacity: indicator ? 1 : 0,
            }}
          />
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={resolveHref(link.href)}
              onMouseEnter={handleMouseEnter}
              className="header-nav-link font-body text-sm text-muted-foreground hover:text-foreground transition-colors pb-1"
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://ashley-bass.clientsecure.me/request/service"
            target="_blank"
            rel="noopener noreferrer"
            className="header-cta ml-2 inline-block bg-primary text-primary-foreground px-5 py-2 rounded-md font-body font-medium tracking-wider text-xs uppercase hover:opacity-90 transition-opacity"
          >
            Get Started
          </a>
        </nav>

        {/* Mobile toggle */}
        <button
          className="header-mobile-toggle lg:hidden p-2 text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="header-nav-mobile lg:hidden border-t border-border bg-background px-6 py-4 flex flex-col gap-3">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={resolveHref(link.href)}
              onClick={() => setMobileOpen(false)}
              className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://ashley-bass.clientsecure.me/request/service"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="mt-2 inline-block text-center bg-primary text-primary-foreground px-5 py-2 rounded-md font-body font-medium tracking-wider text-xs uppercase hover:opacity-90 transition-opacity"
          >
            Get Started
          </a>
        </nav>
      )}
    </header>
  );
};

export default Header;
